import React, { useState } from 'react'
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, FormControl, InputLabel, Select, MenuItem, IconButton, Link as MuiLink } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { router, usePage, Link } from '@inertiajs/react'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import FormattedCurrency from '../../../../components/ui/FormattedCurrency'

Show.layout = (page) => <AppShell>{page}</AppShell>

const ACCOUNT_TYPE_GROUPS = {
  '1': '1xxx — Finanzkonten',
  '2': '2xxx — Verbindlichkeiten',
  '3': '3xxx — Rücklagen',
  '4': '4xxx — Erlöse',
  '5': '5xxx — Instandhaltung',
  '6': '6xxx — Verwaltungskosten',
  '7': '7xxx — Betriebskosten',
  '9': '9xxx — Vorträge',
}

export default function Show({ property, fiscal_year, fiscal_years = [], accounts = [] }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}`
  const [expandedParents, setExpandedParents] = useState({})

  const onChangeFiscalYear = (id) => {
    router.visit(`${basePath}/balance_sheet?fiscal_year_id=${id}`)
  }

  const toggleParent = (id) => {
    setExpandedParents((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  // Only top-level (non sub_account) rows are rendered directly; children are revealed per super-account.
  const topLevel = accounts.filter((a) => !a.sub_account)
  const childrenByParent = {}
  accounts.forEach((a) => {
    if (a.sub_account && a.parent_id != null) {
      if (!childrenByParent[a.parent_id]) childrenByParent[a.parent_id] = []
      childrenByParent[a.parent_id].push(a)
    }
  })

  const groups = {}
  topLevel.forEach((a) => {
    const key = a.number[0]
    if (!groups[key]) groups[key] = []
    groups[key].push(a)
  })

  const sumGroup = (group, field) => group.reduce((acc, a) => acc + parseFloat(a[field] || 0), 0)

  return (
    <Box>
      <PageHeader
        title="Saldenliste"
        breadcrumbs={[
          { label: property.name, href: basePath },
          { label: 'Saldenliste' },
        ]}
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Geschäftsjahr</InputLabel>
          <Select
            value={fiscal_year.id}
            label="Geschäftsjahr"
            onChange={(e) => onChangeFiscalYear(e.target.value)}
            data-testid="fiscal-year-filter"
          >
            {fiscal_years.map((fy) => (
              <MenuItem key={fy.id} value={fy.id}>{fy.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([key, groupAccounts]) => (
        <Paper key={key} elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ px: 2, py: 1.25, backgroundColor: 'background.subtle', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{ACCOUNT_TYPE_GROUPS[key] || `${key}xxx`}</Typography>
          </Box>
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { height: 40, py: 0 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 32, px: 0 }} />
                  <TableCell sx={{ fontWeight: 600, width: 140 }}>Konto</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Bezeichnung</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Eröffnungsbilanz</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Soll</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Haben</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Saldo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupAccounts.map((account) => {
                  const accountUrl = `${basePath}/ledger_accounts/${account.id}?fiscal_year_id=${fiscal_year.id}`
                  const children = childrenByParent[account.id] || []
                  const isExpanded = !!expandedParents[account.id]
                  return (
                    <React.Fragment key={account.id}>
                      <TableRow hover data-testid={`account-row-${account.id}`}>
                        <TableCell sx={{ width: 32, px: 0, textAlign: 'center' }}>
                          {children.length > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => toggleParent(account.id)}
                              aria-label={isExpanded ? 'Einklappen' : 'Ausklappen'}
                              data-testid={`toggle-account-${account.id}`}
                              sx={{ p: 0.25 }}
                            >
                              {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          <MuiLink component={Link} href={accountUrl} underline="hover" color="inherit">
                            {account.number}
                          </MuiLink>
                        </TableCell>
                        <TableCell>
                          <MuiLink component={Link} href={accountUrl} underline="hover" color="inherit">
                            {account.name}
                          </MuiLink>
                        </TableCell>
                        <TableCell align="right"><FormattedCurrency value={account.opening_balance} /></TableCell>
                        <TableCell align="right"><FormattedCurrency value={account.period_debit} /></TableCell>
                        <TableCell align="right"><FormattedCurrency value={account.period_credit} /></TableCell>
                        <TableCell align="right"><FormattedCurrency value={account.closing_balance} /></TableCell>
                      </TableRow>
                      {isExpanded && children.map((sub) => {
                        const subUrl = `${basePath}/ledger_accounts/${sub.id}?fiscal_year_id=${fiscal_year.id}`
                        return (
                          <TableRow key={sub.id} sx={{ backgroundColor: 'background.subtle' }} data-testid={`account-row-${sub.id}`}>
                            <TableCell sx={{ width: 32, px: 0 }} />
                            <TableCell sx={{ pl: 4, color: 'text.secondary' }}>
                              <MuiLink component={Link} href={subUrl} underline="hover" color="inherit">
                                {sub.number}
                              </MuiLink>
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              <MuiLink component={Link} href={subUrl} underline="hover" color="inherit">
                                {sub.name}
                              </MuiLink>
                            </TableCell>
                            <TableCell align="right"><FormattedCurrency value={sub.opening_balance} /></TableCell>
                            <TableCell align="right"><FormattedCurrency value={sub.period_debit} /></TableCell>
                            <TableCell align="right"><FormattedCurrency value={sub.period_credit} /></TableCell>
                            <TableCell align="right"><FormattedCurrency value={sub.closing_balance} /></TableCell>
                          </TableRow>
                        )
                      })}
                    </React.Fragment>
                  )
                })}
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell sx={{ width: 32, px: 0 }} />
                  <TableCell colSpan={2} sx={{ fontWeight: 600 }}>Summe</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}><FormattedCurrency value={sumGroup(groupAccounts, 'opening_balance')} /></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}><FormattedCurrency value={sumGroup(groupAccounts, 'period_debit')} /></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}><FormattedCurrency value={sumGroup(groupAccounts, 'period_credit')} /></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}><FormattedCurrency value={sumGroup(groupAccounts, 'closing_balance')} /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}

      {accounts.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
          <Typography color="text.secondary">Keine Konten vorhanden.</Typography>
        </Paper>
      )}
    </Box>
  )
}
